import React, { useState } from 'react';
import { usePreferences } from '../state/usePreferences';
import TickBox from '../components/TickBox';
import Drawer from '../components/Drawer';
import YouTubePlayer from '../components/YouTubePlayer';
import ReelPicker from './ReelPicker';
import { usePlaylistVideos } from '../hooks/usePlaylistVideos';
import { listIdOf, splitVideoFields } from './videoLayout';
const thumbOf = id => `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;
const has = (list, id) => (list || []).some(x => String(x) === String(id));

// The card shows the cover ops uploaded. Without one it falls back to the
// playlist's own first video, which is why the queue is read either way.
const PlaylistCard = ({
  option,
  checked,
  atLimit,
  locked,
  onOpen,
  onToggle
}) => {
  const {
    ids
  } = usePlaylistVideos(listIdOf(option));
  const cover = option.cover_url || (ids[0] ? thumbOf(ids[0]) : '');
  const capped = !checked && atLimit;
  return React.createElement("div", {
    className: `cp-tile${checked ? ' is-on' : ''}${locked && !checked ? ' is-locked' : ''}`
  }, React.createElement("button", {
    type: "button",
    className: `cp-tile-photo${cover ? '' : ' is-blank'}`,
    style: cover ? {
      backgroundImage: `url(${cover})`
    } : undefined,
    onClick: onOpen,
    "aria-label": `Open ${option.name}`
  }, cover && React.createElement("span", {
    className: "cp-tile-scrim"
  }), React.createElement("span", {
    className: "cp-tile-name"
  }, option.name), cover && React.createElement("span", {
    className: "cp-tile-view"
  }, "View")), React.createElement("button", {
    type: "button",
    className: `cp-tickrow${checked ? ' is-on' : ''}${capped ? ' is-capped' : ''}`,
    disabled: locked || capped,
    onClick: onToggle,
    "aria-label": `${checked ? 'Deselect' : 'Select'} ${option.name}`
  }, React.createElement(TickBox, {
    checked: checked
  }), React.createElement("span", null, ids.length ? `${ids.length} videos` : 'Loading…')));
};

// The player plus that playlist's videos. A playlist is chosen whole, so the
// rail plays videos rather than picking them.
const PlaylistDrawerBody = ({
  option
}) => {
  const {
    ids,
    titles,
    failed
  } = usePlaylistVideos(listIdOf(option));
  const [nowPlaying, setNowPlaying] = useState(null);
  const current = nowPlaying || ids[0] || null;
  if (failed || !ids.length) {
    return React.createElement("div", {
      className: "cp-higrid is-solo"
    }, React.createElement("div", {
      className: "cp-playerbox"
    }, React.createElement("div", {
      className: "cp-player"
    }, React.createElement("iframe", {
      title: option.name,
      src: option.embed_url,
      allow: "accelerometer; autoplay; encrypted-media; picture-in-picture",
      allowFullScreen: true
    }))));
  }
  return React.createElement("div", {
    className: "cp-higrid"
  }, React.createElement("div", {
    className: "cp-playerbox"
  }, React.createElement("div", {
    className: "cp-player"
  }, React.createElement(YouTubePlayer, {
    videoId: current,
    title: option.name
  }))), React.createElement("div", {
    className: "cp-ytrail"
  }, React.createElement("div", {
    className: "cp-ytrail-head"
  }, React.createElement("span", {
    className: "cp-ytrail-name"
  }, option.name), React.createElement("span", {
    className: "cp-ytrail-count"
  }, ids.indexOf(current) + 1, " / ", ids.length)), React.createElement("div", {
    className: "cp-ytrail-list"
  }, ids.slice(0, 24).map((id, i) => {
    const playing = id === current;
    return React.createElement("div", {
      className: `cp-ytrow${playing ? ' is-on' : ''}`,
      key: id
    }, React.createElement("button", {
      type: "button",
      className: "cp-ytrow-main",
      onClick: () => setNowPlaying(id),
      "aria-label": `Play video ${i + 1}`
    }, React.createElement("span", {
      className: "cp-ytthumb",
      style: {
        backgroundImage: `url(${thumbOf(id)})`
      }
    }, React.createElement("span", {
      className: "cp-ytthumb-scrim"
    }), React.createElement("span", {
      className: "cp-ytbadge"
    }, i + 1)), React.createElement("span", {
      className: "cp-ytmeta"
    }, React.createElement("span", {
      className: "cp-yttitle"
    }, titles[id] || `Video ${i + 1}`), React.createElement("span", {
      className: "cp-ytsub"
    }, playing ? 'Now playing' : 'Tap to play'))));
  }))));
};

/**
 * Highlights and Reels as two tabs of one section. Which field plays which
 * role is decided by the payload, not by its key.
 */
const VideosSection = ({
  section,
  group,
  locked,
  isPhone,
  onToggle
}) => {
  const {
    picker,
    sources
  } = splitVideoFields(section);
  const {
    tab,
    setTab
  } = usePreferences();
  const [openId, setOpenId] = useState(null);
  const pickedLists = group[picker.key] || [];
  const listCap = picker.max_picks || 0;
  const atListLimit = listCap > 0 && pickedLists.length >= listCap;
  const open = (picker.options || []).find(o => String(o.id) === String(openId));
  const openOn = open ? has(pickedLists, open.id) : false;
  const onHighlights = tab === 0;
  return React.createElement(React.Fragment, null, React.createElement("div", {
    className: "cp-vtabs"
  }, React.createElement("button", {
    type: "button",
    className: `cp-vtab${onHighlights ? ' is-on' : ''}`,
    onClick: () => setTab(0)
  }, "Highlights"), React.createElement("button", {
    type: "button",
    className: `cp-vtab${onHighlights ? '' : ' is-on'}`,
    onClick: () => setTab(1)
  }, "Reels")), onHighlights ? React.createElement(React.Fragment, null, React.createElement("div", {
    className: "cp-cardgrid"
  }, (picker.options || []).map(option => React.createElement(PlaylistCard, {
    key: option.id,
    option: option,
    checked: has(pickedLists, option.id),
    atLimit: atListLimit,
    locked: locked,
    onOpen: () => setOpenId(option.id),
    onToggle: () => !locked && onToggle(picker.key, option.id, listCap)
  }))), React.createElement(Drawer, {
    open: !!open,
    title: open?.name,
    meta: `${pickedLists.length} / ${listCap} playlists selected`,
    checked: openOn,
    tickLabel: openOn ? 'Playlist selected' : 'Select this playlist',
    locked: locked || atListLimit && !openOn,
    fill: true,
    onToggle: () => open && !locked && onToggle(picker.key, open.id, listCap),
    onClose: () => setOpenId(null)
  }, open && React.createElement(PlaylistDrawerBody, {
    option: open
  }))) : sources.map(field => React.createElement(ReelPicker, {
    key: field.key,
    field: field,
    picked: group[field.key] || [],
    locked: locked,
    isPhone: isPhone,
    onToggle: id => onToggle(field.key, id, field.max_picks)
  })));
};
export default VideosSection;
